import { faCalendar } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { addDays, format, isAfter, subDays } from "date-fns";
import queryString from "query-string";
import React, { useCallback, useState } from "react";
import { Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useIntl } from "react-intl";
import { useChainData } from "../../../components/chain-data-provider";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import { useProperty } from "../../../components/property-provider";
import { useUserProfile } from "../../../components/user-profile-provider";
import { useConfig } from "../../../lib/config";
import { messages } from "./bookingForm.messages";
import { useBookingFormConfiguration } from "./hooks/useBookingFormConfiguration";
import { useOccupancyForm } from "./hooks/useOccupancyForm";
import { AgeBasedOccupancy } from "./components/AgeBasedOccupancy";
import { TypeBasedOccupancy } from "./components/TypeBasedOccupancy";
import {
  ActionsRow,
  CalendarIcon,
  Card,
  Field,
  FieldGrid,
  FieldLabel,
  PageWrapper,
  SectionLabel,
  Title,
} from "./styles";

interface BookingFormProps {
  embedded?: boolean;
  secondaryAction?: React.ReactNode;
}

export function BookingForm({ embedded = false, secondaryAction }: BookingFormProps) {
  const intl = useIntl();
  const config = useConfig();
  const propertyInfo = useProperty();
  const chainData = useChainData();
  const userProfile = useUserProfile();

  const { maxOccupancy, occupancyOptions } = useBookingFormConfiguration();
  const {
    occupancy,
    childrenAges,
    stepOccupancy,
    stepChildrenCount,
    changeChildAge,
  } = useOccupancyForm();

  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);

  const filterByAge = Boolean(occupancyOptions?.filterByAge);

  const handleCheckInChange = useCallback((date: Date | null) => {
    setCheckIn(date);
    setCheckOut(current => {
      if (!date) return current;
      if (current && isAfter(current, date)) return current;
      return addDays(date, 1);
    });
  }, []);

  const handleSubmit = useCallback(() => {
    if (!checkIn || !checkOut) return;

    const selectedAges = childrenAges.filter((age): age is number => age !== null);
    const occupancyQuery = filterByAge
      ? {
          guestAges: selectedAges.length ? selectedAges.map(String) : undefined,
        }
      : {
          juniors: occupancy.juniors || undefined,
          children: occupancy.children || undefined,
          babies: occupancy.babies || undefined,
        };

    const engineUrl = queryString.stringifyUrl({
      url: `${config.engineBaseUrl}/${chainData?.isChain ? "chain" : "hotel"}/${
        propertyInfo.ticker
      }`,
      query: {
        datein: format(checkIn, "dd-MM-yyyy"),
        dateout: format(checkOut, "dd-MM-yyyy"),
        adults: occupancy.adults,
        ...occupancyQuery,
        origin: "backoffice",
        disableAbTest: "1",
        encodedAffiliate: userProfile ? btoa(userProfile.email || "") : undefined,
      },
    });
    window.open(engineUrl, "_blank");
  }, [
    config.engineBaseUrl,
    chainData?.isChain,
    propertyInfo.ticker,
    checkIn,
    checkOut,
    filterByAge,
    childrenAges,
    occupancy,
    userProfile,
  ]);

  const allAgesSelected = childrenAges.every(age => age !== null);
  const isValid =
    Boolean(checkIn) &&
    Boolean(checkOut) &&
    isAfter(checkOut!, checkIn!) &&
    (!filterByAge || allAgesSelected);

  return (
    <PageWrapper embedded={embedded}>
      {!embedded && <Title>{intl.formatMessage(messages.title)}</Title>}
      <Card embedded={embedded}>
        <FieldGrid>
          <Field>
            <FieldLabel htmlFor="bookingFormCheckIn">
              {intl.formatMessage(messages.checkIn)}
            </FieldLabel>
            <DatePicker
              id="bookingFormCheckIn"
              selected={checkIn}
              onChange={date => handleCheckInChange(date as Date | null)}
              selectsStart
              startDate={checkIn}
              endDate={checkOut}
              maxDate={checkOut ? subDays(checkOut, 1) : undefined}
              minDate={new Date()}
              dateFormat="dd/MM/yyyy"
              placeholderText="dd/mm/aaaa"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
            <CalendarIcon>
              <FontAwesomeIcon icon={faCalendar} />
            </CalendarIcon>
          </Field>

          <Field>
            <FieldLabel htmlFor="bookingFormCheckOut">
              {intl.formatMessage(messages.checkOut)}
            </FieldLabel>
            <DatePicker
              id="bookingFormCheckOut"
              selected={checkOut}
              onChange={date => setCheckOut(date as Date | null)}
              selectsEnd
              startDate={checkIn}
              endDate={checkOut}
              minDate={checkIn ? addDays(checkIn, 1) : new Date()}
              dateFormat="dd/MM/yyyy"
              placeholderText="dd/mm/aaaa"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
            <CalendarIcon>
              <FontAwesomeIcon icon={faCalendar} />
            </CalendarIcon>
          </Field>
        </FieldGrid>

        <SectionLabel>{intl.formatMessage(messages.occupancy)}</SectionLabel>

        {filterByAge && occupancyOptions ? (
          <AgeBasedOccupancy
            occupancy={occupancy}
            childrenAges={childrenAges}
            maxOccupancy={maxOccupancy}
            occupancyOptions={occupancyOptions}
            onStepOccupancy={stepOccupancy}
            onStepChildrenCount={stepChildrenCount}
            onChangeChildAge={changeChildAge}
          />
        ) : (
          <TypeBasedOccupancy
            occupancy={occupancy}
            maxOccupancy={maxOccupancy}
            onStep={stepOccupancy}
          />
        )}

        <ActionsRow>
          {secondaryAction}
          <Button
            className="px-4 py-2"
            variant="primary"
            type="button"
            disabled={!isValid}
            onClick={handleSubmit}>
            {intl.formatMessage(messages.submit)}
          </Button>
        </ActionsRow>
      </Card>
    </PageWrapper>
  );
}

export default withAuthorizationVerifier(BookingForm);
